import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Typography } from '@mui/material';
import clsx from 'clsx';
import {
  ConditionalTemplateQuestion_CreateConditionalTemplateQuestionInput,
  ConditionalTemplateQuestion_FieldsFragment,
  ConditionalTemplateQuestion_GetAllConditionalTemplateQuestionsDocument,
  ConditionalTemplateQuestion_GetConditionalTemplateQuestionsByConditionalTemplateQuestionSetIdDocument,
  ConditionalTemplateQuestion_UpdateConditionalTemplateQuestionInput,
  useConditionalTemplateQuestion_CreateConditionalTemplateQuestionMutation,
  useConditionalTemplateQuestion_UpdateConditionalTemplateQuestionMutation,
} from 'queries';
import * as yup from 'yup';

import FormTextField from 'common/form-text-field';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

import { useStyles } from './conditional-template-question-form.styles';

type Props = {
  conditionalTemplateQuestionSetId: string;
  isEditing: boolean;
  selectedConditionalTemplateQuestion?: ConditionalTemplateQuestion_FieldsFragment | undefined;
  onExitClick: () => void;
};

type ConditionalTemplateQuestionFormValues = {
  question: string;
};

const requiredTextFields = ['question'];
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const requiredTextFieldValidation = requiredTextFields.reduce((acc: any, val: string) => {
  acc[val] = yup.string().required('Required');
  return acc;
}, {});

const schema = yup.object().shape({
  ...requiredTextFieldValidation,
});

export const ConditionalTemplateQuestionForm = ({
  conditionalTemplateQuestionSetId,
  isEditing,
  selectedConditionalTemplateQuestion,
  onExitClick,
}: Props) => {
  const classes = useStyles();

  const {
    handleSubmit,
    formState: { isSubmitting, errors },
    control,
  } = useForm<ConditionalTemplateQuestionFormValues>({
    // @ts-expect-error - RHF V7 limitation #7895
    resolver: yupResolver(schema),
    defaultValues: {
      question: selectedConditionalTemplateQuestion?.question || '',
    },
  });

  const [
    createConditionalTemplateQuestion,
    { loading: executingCreateConditionalTemplateQuestionMutation },
  ] = useConditionalTemplateQuestion_CreateConditionalTemplateQuestionMutation({
    refetchQueries: [
      {
        query:
          ConditionalTemplateQuestion_GetConditionalTemplateQuestionsByConditionalTemplateQuestionSetIdDocument,
        variables: {
          input: { conditionalTemplateQuestionSetId },
        },
      },
    ],
    onCompleted: () => {
      showToast({
        message: 'Conditional template question created.',
        severity: NotificationSeverity.SUCCESS,
      });
      onExitClick();
    },
    onError: (e) => {
      showAlert({
        message: 'Error creating conditional template question: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [
    updateConditionalTemplateQuestion,
    { loading: executingUpdateConditionalTemplateQuestionMutation },
  ] = useConditionalTemplateQuestion_UpdateConditionalTemplateQuestionMutation({
    refetchQueries: [
      { query: ConditionalTemplateQuestion_GetAllConditionalTemplateQuestionsDocument },
    ],
    onCompleted: () => {
      showToast({
        message: 'Conditional template question updated',
        severity: NotificationSeverity.SUCCESS,
      });
      onExitClick();
    },
    onError: (e) => {
      showAlert({
        message: 'Error updating conditional template question: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const submitForm = async ({ ...formData }: ConditionalTemplateQuestionFormValues) => {
    if (selectedConditionalTemplateQuestion) {
      const input: ConditionalTemplateQuestion_UpdateConditionalTemplateQuestionInput = {
        ...formData,
        id: selectedConditionalTemplateQuestion.id,
      };

      updateConditionalTemplateQuestion({
        variables: {
          input,
        },
      });
    } else {
      const input: ConditionalTemplateQuestion_CreateConditionalTemplateQuestionInput = {
        ...formData,
        conditionalTemplateQuestionSetId,
      };

      createConditionalTemplateQuestion({
        variables: {
          input,
        },
      });
    }
  };

  const isDisabled =
    isSubmitting ||
    executingCreateConditionalTemplateQuestionMutation ||
    executingUpdateConditionalTemplateQuestionMutation;

  return (
    <>
      <Typography variant="h5" className={classes.h5}>
        {isEditing ? 'Edit' : 'Create'} Conditional Template Question
      </Typography>
      <form
        id="conditional-template-question-form"
        className={classes.form}
        onSubmit={handleSubmit(submitForm)}
      >
        <Box className={classes.formSection}>
          <Box className={classes.container}>
            <Controller
              name="question"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  autoFocus
                  inputProps={{ maxLength: 300 }}
                  label="Question"
                  errorMessage={errors.question?.message}
                />
              )}
            />
          </Box>

          <Box className={clsx(classes.container, classes.submitButton)}>
            <Button
              type="submit"
              form="conditional-template-question-form"
              color="secondary"
              variant="contained"
              disabled={isDisabled}
            >
              Save
            </Button>
          </Box>
        </Box>
      </form>
    </>
  );
};
